/**
 * Renders a purchase order to a PDF laid out like the PO Template sheet: company
 * header, the PO/date/requested-by/status row, side-by-side vendor and ship-to
 * blocks, the line-item table, the subtotal/shipping/processing fee/tax/total stack, notes and
 * terms, and the approval signature line.
 *
 * Returned as a Blob so the caller can post it straight back as the PO's first
 * attachment.
 */

import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFPage } from 'pdf-lib';

const PAGE_W = 612;
const PAGE_H = 792;
const MARGIN = 48;
const CONTENT_W = PAGE_W - MARGIN * 2;

const NAVY = rgb(0.16, 0.26, 0.37);
const GREY = rgb(0.35, 0.44, 0.55);
const BLACK = rgb(0.1, 0.11, 0.12);
const LINE = rgb(0.8, 0.83, 0.87);
const HEADER_FILL = rgb(0.94, 0.96, 0.98);

export const COMPANY_NAME = 'TECHFORCE ROBOTICS';
export const COMPANY_ADDRESS = '26151 Jefferson Ave., Murrieta, CA 92562';

export interface PurchaseOrderPdfItem {
  description: string;
  qty: number;
  unitPrice: number;
}

export interface PurchaseOrderPdfData {
  poNumber: string;
  dateIssued: string;
  requestedBy: string;
  status: string;

  vendorName: string;
  vendorAddress: string;
  vendorContactPerson: string;
  vendorPhoneEmail: string;
  paymentTerms: string;

  shipToName: string;
  shipToAddress: string;
  shipToAttn: string;
  shipToPhoneEmail: string;
  requestedDelivery: string;
  expectedDelivery: string;

  items: PurchaseOrderPdfItem[];
  shipping: number;
  processingFee: number;
  tax: number;
  notesTerms: string;
  approvedBy: string;
  approvedDate: string;
}

function money(value: number): string {
  return `$${(Number.isFinite(value) ? value : 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function qtyText(value: number): string {
  return Number.isInteger(value) ? String(value) : String(value);
}

export async function generatePurchaseOrderPdf(data: PurchaseOrderPdfData): Promise<Blob> {
  const doc = await PDFDocument.create();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);

  let page: PDFPage = doc.addPage([PAGE_W, PAGE_H]);
  let y = PAGE_H - MARGIN;

  const newPage = () => {
    page = doc.addPage([PAGE_W, PAGE_H]);
    y = PAGE_H - MARGIN;
  };
  const ensure = (needed: number) => {
    if (y - needed < MARGIN) newPage();
  };

  const wrap = (text: string, f: PDFFont, size: number, maxW: number): string[] => {
    const source = String(text ?? '').replace(/\r/g, '');
    const out: string[] = [];
    for (const paragraph of source.split('\n')) {
      const words = paragraph.split(/\s+/).filter(Boolean);
      if (words.length === 0) {
        out.push('');
        continue;
      }
      let cur = '';
      for (const w of words) {
        const test = cur ? `${cur} ${w}` : w;
        if (f.widthOfTextAtSize(test, size) > maxW && cur) {
          out.push(cur);
          cur = w;
        } else {
          cur = test;
        }
      }
      if (cur) out.push(cur);
    }
    return out.length > 0 ? out : [''];
  };

  const draw = (
    s: string,
    x: number,
    baselineY: number,
    opts: { size?: number; f?: PDFFont; color?: ReturnType<typeof rgb> } = {}
  ) => {
    page.drawText(s, {
      x,
      y: baselineY,
      size: opts.size ?? 9.5,
      font: opts.f ?? font,
      color: opts.color ?? BLACK,
    });
  };

  /** Right-aligns text so the money column lines up. */
  const drawRight = (
    s: string,
    rightX: number,
    baselineY: number,
    opts: { size?: number; f?: PDFFont; color?: ReturnType<typeof rgb> } = {}
  ) => {
    const size = opts.size ?? 9.5;
    const f = opts.f ?? font;
    draw(s, rightX - f.widthOfTextAtSize(s, size), baselineY, opts);
  };

  // ── Company header ──
  draw(COMPANY_NAME, MARGIN, y - 16, { size: 16, f: bold, color: NAVY });
  y -= 22;
  draw(COMPANY_ADDRESS, MARGIN, y - 10, { size: 9, color: GREY });
  y -= 20;
  draw('PURCHASE ORDER', MARGIN, y - 14, { size: 14, f: bold, color: BLACK });
  y -= 22;
  page.drawLine({ start: { x: MARGIN, y }, end: { x: PAGE_W - MARGIN, y }, thickness: 1, color: NAVY });
  y -= 16;

  // ── PO number / date issued / requested by / status ──
  const metaCols: Array<[string, string]> = [
    ['PO Number:', data.poNumber],
    ['Date Issued:', data.dateIssued],
    ['Requested By:', data.requestedBy],
    ['Status:', data.status],
  ];
  const metaColW = CONTENT_W / metaCols.length;
  metaCols.forEach(([label, value], idx) => {
    const x = MARGIN + idx * metaColW;
    draw(label, x, y - 9, { size: 7.5, f: bold, color: GREY });
    const lines = wrap(value || '—', bold, 9.5, metaColW - 8);
    draw(lines[0], x, y - 22, { size: 9.5, f: bold });
  });
  y -= 34;

  // ── Vendor / Ship To, side by side ──
  const colW = (CONTENT_W - 16) / 2;
  const rightX = MARGIN + colW + 16;

  const blockRows = (rows: Array<[string, string]>, x: number, startY: number): number => {
    let cursor = startY;
    for (const [label, value] of rows) {
      const top = cursor;
      draw(label, x, top - 9, { size: 7.5, f: bold, color: GREY });
      const lines = wrap(value || '—', font, 9, colW);
      lines.forEach((line, i) => draw(line, x, top - 20 - i * 11, { size: 9 }));
      cursor = top - (22 + (lines.length - 1) * 11);
    }
    return cursor;
  };

  page.drawRectangle({ x: MARGIN, y: y - 14, width: colW, height: 16, color: HEADER_FILL });
  page.drawRectangle({ x: rightX, y: y - 14, width: colW, height: 16, color: HEADER_FILL });
  draw('VENDOR', MARGIN + 6, y - 10, { size: 8.5, f: bold, color: NAVY });
  draw('SHIP TO', rightX + 6, y - 10, { size: 8.5, f: bold, color: NAVY });
  y -= 22;

  const vendorEnd = blockRows(
    [
      ['Vendor Name:', data.vendorName],
      ['Address:', data.vendorAddress],
      ['Contact Person:', data.vendorContactPerson],
      ['Phone / Email:', data.vendorPhoneEmail],
      ['Payment Terms:', data.paymentTerms],
    ],
    MARGIN,
    y
  );
  const shipEnd = blockRows(
    [
      ['Ship To:', data.shipToName],
      ['Address:', data.shipToAddress],
      ['Attn:', data.shipToAttn],
      ['Phone / Email:', data.shipToPhoneEmail],
      ['Requested Delivery:', data.requestedDelivery],
      ['Expected Delivery:', data.expectedDelivery],
    ],
    rightX,
    y
  );
  y = Math.min(vendorEnd, shipEnd) - 6;
  page.drawLine({ start: { x: MARGIN, y }, end: { x: PAGE_W - MARGIN, y }, thickness: 0.75, color: LINE });
  y -= 16;

  // ── Line items ──
  const ITEM_W = 42;
  const QTY_W = 48;
  const PRICE_W = 78;
  const TOTAL_W = 82;
  const DESC_W = CONTENT_W - ITEM_W - QTY_W - PRICE_W - TOTAL_W;
  const xItem = MARGIN;
  const xDesc = xItem + ITEM_W;
  const xQtyRight = xDesc + DESC_W + QTY_W - 6;
  const xPriceRight = xDesc + DESC_W + QTY_W + PRICE_W - 6;
  const xTotalRight = PAGE_W - MARGIN - 6;

  const drawItemsHeader = () => {
    page.drawRectangle({ x: MARGIN, y: y - 14, width: CONTENT_W, height: 16, color: HEADER_FILL });
    draw('Item #', xItem + 4, y - 10, { size: 8, f: bold, color: NAVY });
    draw('Description', xDesc + 4, y - 10, { size: 8, f: bold, color: NAVY });
    drawRight('Qty', xQtyRight, y - 10, { size: 8, f: bold, color: NAVY });
    drawRight('Unit Price', xPriceRight, y - 10, { size: 8, f: bold, color: NAVY });
    drawRight('Line Total', xTotalRight, y - 10, { size: 8, f: bold, color: NAVY });
    y -= 20;
  };
  drawItemsHeader();

  let subtotal = 0;
  data.items.forEach((item, idx) => {
    const lineTotal = item.qty * item.unitPrice;
    subtotal += lineTotal;
    const descLines = wrap(item.description, font, 9, DESC_W - 8);
    const rowH = Math.max(16, descLines.length * 11 + 5);
    if (y - rowH < MARGIN + 40) {
      newPage();
      drawItemsHeader();
    }
    draw(`${idx + 1}`, xItem + 4, y - 10, { size: 9 });
    descLines.forEach((line, i) => draw(line, xDesc + 4, y - 10 - i * 11, { size: 9 }));
    drawRight(qtyText(item.qty), xQtyRight, y - 10, { size: 9 });
    drawRight(money(item.unitPrice), xPriceRight, y - 10, { size: 9 });
    drawRight(money(lineTotal), xTotalRight, y - 10, { size: 9 });
    y -= rowH;
    page.drawLine({ start: { x: MARGIN, y: y + 3 }, end: { x: PAGE_W - MARGIN, y: y + 3 }, thickness: 0.4, color: LINE });
  });

  // ── Totals ──
  const total = subtotal + data.shipping + data.processingFee + data.tax;
  const totalsRows: Array<[string, number, boolean]> = [
    ['Subtotal:', subtotal, false],
    ['Shipping:', data.shipping, false],
    // Most orders carry no processing fee, and a $0.00 row would only add noise for the vendor.
    ...(data.processingFee > 0 ? [['Processing Fee:', data.processingFee, false] as [string, number, boolean]] : []),
    ['Tax:', data.tax, false],
    ['TOTAL:', total, true],
  ];
  ensure(totalsRows.length * 16 + 10);
  y -= 8;
  for (const [label, value, emphasize] of totalsRows) {
    if (emphasize) {
      page.drawLine({
        start: { x: xDesc + DESC_W, y: y + 2 },
        end: { x: PAGE_W - MARGIN, y: y + 2 },
        thickness: 0.75,
        color: NAVY,
      });
      y -= 4;
    }
    drawRight(label, xPriceRight, y - 10, { size: 9, f: bold, color: emphasize ? NAVY : GREY });
    drawRight(money(value), xTotalRight, y - 10, { size: emphasize ? 10 : 9, f: bold, color: BLACK });
    y -= 15;
  }

  // ── Notes / Terms ──
  y -= 12;
  ensure(60);
  draw('Notes / Terms:', MARGIN, y - 10, { size: 8.5, f: bold, color: NAVY });
  y -= 20;
  const noteLines = wrap(data.notesTerms || '—', font, 9, CONTENT_W);
  for (const line of noteLines) {
    ensure(13);
    draw(line, MARGIN, y - 9, { size: 9 });
    y -= 12;
  }

  // ── Approval ──
  y -= 22;
  ensure(50);
  draw('Approved By (Signature):', MARGIN, y - 10, { size: 8.5, f: bold, color: GREY });
  draw(data.approvedBy || '', MARGIN + 132, y - 10, { size: 9.5, f: bold });
  page.drawLine({
    start: { x: MARGIN + 128, y: y - 14 },
    end: { x: MARGIN + 128 + 200, y: y - 14 },
    thickness: 0.6,
    color: LINE,
  });
  draw('Date:', PAGE_W - MARGIN - 130, y - 10, { size: 8.5, f: bold, color: GREY });
  draw(data.approvedDate || '', PAGE_W - MARGIN - 95, y - 10, { size: 9.5 });
  page.drawLine({
    start: { x: PAGE_W - MARGIN - 99, y: y - 14 },
    end: { x: PAGE_W - MARGIN, y: y - 14 },
    thickness: 0.6,
    color: LINE,
  });

  const bytes = await doc.save();
  // Copy into a fresh buffer so the Blob does not depend on pdf-lib's view.
  return new Blob([new Uint8Array(bytes)], { type: 'application/pdf' });
}

export function purchaseOrderPdfFileName(poNumber: string): string {
  return `${(poNumber || 'purchase-order').replace(/[^\w.-]+/g, '-')}.pdf`;
}
